import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, CheckCircle2, RefreshCw, ScanFace } from 'lucide-react';
import { toast } from 'sonner';
import { loadFaceModels, getFaceDescriptor } from '@/utils/faceApi';

const REQUIRED_SAMPLES = 3;

// Webcam-based face enrollment. Captures REQUIRED_SAMPLES face descriptors
// in-browser and returns them (plus a selfie) to the parent to persist.
export default function FaceEnrollDialog({ open, onOpenChange, employee, onEnroll }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [samples, setSamples] = useState([]);
  const [selfie, setSelfie] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 480, height: 360 }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      setError('');
    } catch (e) {
      setError('Could not access camera. Please allow camera permission.');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSamples([]); setSelfie(null); setError('');
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try { await loadFaceModels(); } catch (e) { setError('Failed to load face engine.'); }
      if (!cancelled) { setModelsLoading(false); await startCamera(); }
    })();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const captureSelfie = () => {
    const video = videoRef.current;
    if (!video) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 360;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  const handleCapture = async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    try {
      const descriptor = await getFaceDescriptor(videoRef.current);
      if (!descriptor) {
        toast.error('No face detected. Face the camera in good light.');
      } else {
        setSamples((prev) => [...prev, descriptor]);
        if (!selfie) setSelfie(captureSelfie());
        toast.success(`Captured sample ${samples.length + 1}/${REQUIRED_SAMPLES}`);
      }
    } catch (e) {
      toast.error('Capture failed. Try again.');
    } finally {
      setCapturing(false);
    }
  };

  const handleSave = async () => {
    if (samples.length < REQUIRED_SAMPLES) return;
    setSaving(true);
    try {
      await onEnroll(samples, selfie);
      stopCamera();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setSamples([]); setSelfie(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) stopCamera(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="face-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanFace className="h-5 w-5 text-emerald-600" /> Enroll Face — {employee?.name}</DialogTitle>
          <DialogDescription>Capture {REQUIRED_SAMPLES} clear photos. The employee will later verify with their face to mark attendance.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative bg-slate-900 rounded-lg overflow-hidden aspect-[4/3] flex items-center justify-center">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            {(modelsLoading || !cameraReady) && !error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 text-sm gap-2 bg-slate-900/70">
                <Loader2 className="h-6 w-6 animate-spin" />
                {modelsLoading ? 'Loading face engine…' : 'Starting camera…'}
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm gap-2 bg-slate-900/80 p-4 text-center">
                {error}
                <Button size="sm" variant="secondary" onClick={startCamera}>Retry camera</Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: REQUIRED_SAMPLES }).map((_, i) => (
              <div key={i} className={`h-2.5 w-10 rounded-full ${i < samples.length ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            ))}
            <span className="text-xs text-slate-500 ml-2">{samples.length}/{REQUIRED_SAMPLES}</span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {samples.length > 0 && (
            <Button variant="ghost" onClick={reset} className="text-slate-500"><RefreshCw className="h-4 w-4 mr-1" /> Reset</Button>
          )}
          {samples.length < REQUIRED_SAMPLES ? (
            <Button onClick={handleCapture} disabled={!cameraReady || capturing || modelsLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="capture-face-btn">
              {capturing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Capture
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-face-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Save Enrollment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
